import React, { useState, useContext } from 'react';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  HelpCircle, MessageSquare, Send, Loader2, CheckCircle,
  Headphones, Mail, Clock, ArrowLeft, ChevronDown, ChevronUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Footer from '@/components/Footer';

const API = process.env.REACT_APP_BACKEND_URL;

const SupportPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketNumber, setTicketNumber] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  const [formData, setFormData] = useState({
    category: '',
    subject: '',
    message: '',
    email: ''
  });

  const categories = [
    { value: 'tecnico', label: 'Problema tecnico', icon: '🔧' },
    { value: 'account', label: 'Account e accesso', icon: '👤' },
    { value: 'fatturazione', label: 'Fatturazione', icon: '💳' },
    { value: 'verifica', label: 'Verifica profilo', icon: '✓' },
    { value: 'altro', label: 'Altro', icon: '📝' }
  ];

  const quickFaqs = [
    {
      q: "Come posso modificare il mio profilo?",
      a: "Vai alla sezione 'Profilo' dal menu di navigazione e clicca su 'Modifica'. Potrai aggiornare tutte le informazioni del tuo profilo."
    },
    {
      q: "Come funziona la verifica del profilo?",
      a: "La verifica è un processo che conferma la tua identità professionale. Vai sul tuo profilo e clicca 'Richiedi Verifica'. Un nostro operatore esaminerà la richiesta."
    },
    {
      q: "Non riesco ad accedere al mio account",
      a: "Prova a reimpostare la password dalla pagina di login. Se il problema persiste, contattaci indicando l'email associata al tuo account."
    },
    {
      q: "Come posso eliminare il mio account?",
      a: "Puoi richiedere l'eliminazione del tuo account contattando il supporto. Elaboreremo la richiesta entro 30 giorni come previsto dal GDPR."
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category || !formData.subject || !formData.message) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    if (!user && !formData.email) {
      toast.error('Inserisci la tua email per ricevere una risposta');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/support/ticket`, {
        ...formData,
        email: user ? user.email : formData.email
      });
      
      setTicketNumber(response.data.ticket_number);
      setSubmitted(true);
      toast.success('Richiesta inviata con successo!');
    } catch (error) {
      console.error('Error submitting ticket:', error);
      toast.error('Errore nell\'invio della richiesta');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-2xl mx-auto px-6 py-20">
          <Card className="text-center">
            <CardContent className="pt-12 pb-8">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-[#344B71] mb-4">
                Richiesta Inviata!
              </h2>
              <p className="text-slate-600 mb-6">
                Il tuo ticket è stato creato con successo. Ti risponderemo entro 24-48 ore lavorative.
              </p>
              <div className="bg-slate-100 rounded-xl p-4 mb-8">
                <p className="text-sm text-slate-500 mb-1">Numero Ticket</p>
                <p className="text-2xl font-bold text-[#344B71]">{ticketNumber}</p>
              </div>
              <p className="text-sm text-slate-500 mb-6">
                Conserva questo numero per riferimento. Riceverai una conferma via email.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="bg-[#344B71] hover:bg-[#2a3d5c] text-white"
              >
                Torna alla Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#344B71] to-[#2a3d5c] text-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-6 text-white/80 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-white/10 rounded-xl">
              <Headphones className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Centro Assistenza</h1>
              <p className="text-white/80">Siamo qui per aiutarti</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-6">
            {/* Quick Info Cards */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-[#E8A007]/10 rounded-lg">
                    <Mail className="w-5 h-5 text-[#E8A007]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#344B71]">Email</h3>
                    <p className="text-sm text-slate-600">supporto@consultanthub.it</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Clock className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#344B71]">Tempi di risposta</h3>
                    <p className="text-sm text-slate-600">24-48 ore lavorative</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick FAQs */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-[#344B71]" />
                  Domande Frequenti
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {quickFaqs.map((faq, index) => (
                    <div key={index} className="px-6">
                      <button
                        onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                        className="w-full py-4 flex items-center justify-between text-left"
                      >
                        <span className="font-medium text-[#344B71] text-sm pr-4">{faq.q}</span>
                        {expandedFaq === index ? (
                          <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        )}
                      </button>
                      {expandedFaq === index && (
                        <p className="pb-4 text-sm text-slate-600 -mt-2">{faq.a}</p>
                      )}
                    </div>
                  ))}
                </div>
                <div className="p-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/faq')}
                    className="w-full text-[#344B71] border-[#344B71]"
                  >
                    Vedi tutte le FAQ
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#344B71]" />
                  Invia una Richiesta
                </CardTitle>
                <CardDescription>
                  Compila il form e ti risponderemo al più presto
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Categoria *
                    </label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(value) => setFormData({...formData, category: value})}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Seleziona una categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            <span className="flex items-center gap-2">
                              <span>{cat.icon}</span>
                              <span>{cat.label}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Email for non-logged users */}
                  {!user && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        La tua Email *
                      </label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="email@esempio.com"
                        className="h-12"
                      />
                    </div>
                  )}

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Oggetto *
                    </label>
                    <Input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      placeholder="Breve descrizione del problema"
                      className="h-12"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Messaggio *
                    </label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder="Descrivi in dettaglio la tua richiesta o il problema riscontrato..."
                      rows={6}
                      className="resize-none"
                    />
                  </div>

                  {/* Submit */}
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-[#344B71] hover:bg-[#2a3d5c] text-white"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Invio in corso...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Invia Richiesta
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default SupportPage;
